/* ==========================================================================
   ELEPHANT CAFE — booking.js
   Generates bookable time slots from each venue's real trading hours.
   All date maths is done in the venue's timezone (Australia/Melbourne) so a
   customer browsing from overseas still sees the correct day and last seating.
   ========================================================================== */
(function () {
  'use strict';

  var DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  /* ---------- time helpers ---------- */

  function toMinutes(hhmm) {
    if (!hhmm) return null;
    var bits = String(hhmm).split(':');
    var h = parseInt(bits[0], 10);
    var m = parseInt(bits[1], 10) || 0;
    if (isNaN(h)) return null;
    return h * 60 + m;
  }

  function label(mins) {
    var h = Math.floor(mins / 60);
    var m = mins % 60;
    var suffix = h >= 12 ? 'pm' : 'am';
    var h12 = h % 12;
    if (h12 === 0) h12 = 12;
    return h12 + ':' + (m < 10 ? '0' + m : m) + suffix;
  }

  function pad(n) { return n < 10 ? '0' + n : String(n); }

  /* Current wall-clock time at the venue, regardless of the visitor's device. */
  function venueNow(tz) {
    var parts;
    try {
      parts = new Intl.DateTimeFormat('en-AU', {
        timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false
      }).formatToParts(new Date());
    } catch (e) {
      var d = new Date();
      return { y: d.getFullYear(), m: d.getMonth() + 1, d: d.getDate(), minutes: d.getHours() * 60 + d.getMinutes() };
    }
    var o = {};
    parts.forEach(function (p) { o[p.type] = p.value; });
    var hour = parseInt(o.hour, 10) % 24;
    return {
      y: parseInt(o.year, 10),
      m: parseInt(o.month, 10),
      d: parseInt(o.day, 10),
      minutes: hour * 60 + parseInt(o.minute, 10)
    };
  }

  function todayISO(tz) {
    var n = venueNow(tz);
    return n.y + '-' + pad(n.m) + '-' + pad(n.d);
  }

  function addDaysISO(iso, days) {
    var b = iso.split('-');
    var d = new Date(Date.UTC(+b[0], +b[1] - 1, +b[2]));
    d.setUTCDate(d.getUTCDate() + days);
    return d.getUTCFullYear() + '-' + pad(d.getUTCMonth() + 1) + '-' + pad(d.getUTCDate());
  }

  /* Weekday for a plain Y-M-D string, computed in UTC so it never drifts. */
  function weekdayOf(iso) {
    var b = iso.split('-');
    return new Date(Date.UTC(+b[0], +b[1] - 1, +b[2])).getUTCDay();
  }

  function prettyDate(iso) {
    var b = iso.split('-');
    var d = new Date(Date.UTC(+b[0], +b[1] - 1, +b[2]));
    return DAY_NAMES[d.getUTCDay()] + ' ' + d.getUTCDate() + ' ' +
      ['January','February','March','April','May','June','July','August','September','October','November','December'][d.getUTCMonth()];
  }

  /* ---------- venue hours ---------- */

  function hoursFor(venue, iso) {
    if (venue.closedDates && venue.closedDates.indexOf(iso) !== -1) return null;
    var h = venue.hours[String(weekdayOf(iso))];
    if (!h || !h[0] || !h[1]) return null;
    var open = toMinutes(h[0]);
    var close = toMinutes(h[1]);
    if (open === null || close === null || close <= open) return null;
    return { open: open, close: close };
  }

  function lastSeating(venue, hours) {
    return hours.close - (venue.lastSeatingBuffer || 60);
  }

  /* ---------- the service ribbon ---------- */
  /* Shows the whole trading day as one bar so people can see at a glance how
     much of the day is left and where the busy stretch sits. */
  function drawRibbon(root, venue, iso) {
    var track = root.querySelector('[data-ribbon-track]');
    var legend = root.querySelector('[data-ribbon-legend]');
    var caption = root.querySelector('[data-ribbon-caption]');
    if (!track) return;

    var hours = hoursFor(venue, iso);
    track.innerHTML = '';

    if (!hours) {
      track.innerHTML = '<span class="ribbon__seg" data-band="closed" style="width:100%"></span>';
      if (legend) legend.hidden = true;
      if (caption) caption.textContent = venue.name + ' is closed on ' + prettyDate(iso) + '.';
      return;
    }

    if (legend) legend.hidden = false;

    var last = lastSeating(venue, hours);
    var span = hours.close - hours.open;
    var peakFrom = Math.max(hours.open, toMinutes(venue.peakFrom || '09:00'));
    var peakTo = Math.min(last, toMinutes(venue.peakTo || '12:00'));

    var bands = [];
    if (peakFrom > hours.open) bands.push(['early', hours.open, Math.min(peakFrom, last)]);
    if (peakTo > peakFrom) bands.push(['peak', peakFrom, peakTo]);
    if (last > Math.max(peakTo, hours.open)) bands.push(['late', Math.max(peakTo, peakFrom), last]);
    bands.push(['closed', last, hours.close]);

    bands.forEach(function (b) {
      if (b[2] <= b[1]) return;
      var seg = document.createElement('span');
      seg.className = 'ribbon__seg';
      seg.setAttribute('data-band', b[0]);
      seg.style.width = (((b[2] - b[1]) / span) * 100) + '%';
      track.appendChild(seg);
    });

    if (caption) {
      caption.textContent = prettyDate(iso) + ' — kitchen ' + label(hours.open) + ' to ' +
        label(hours.close) + ', last table seated ' + label(last) + '.';
    }
  }

  /* ---------- slots ---------- */

  function buildSlots(root, venue, iso, tz) {
    var wrap = root.querySelector('[data-slots]');
    var note = root.querySelector('[data-slots-note]');
    var hidden = root.querySelector('[data-time-value]');
    if (!wrap) return;

    wrap.innerHTML = '';
    if (hidden) hidden.value = '';

    if (!iso) {
      if (note) { note.textContent = 'Pick a date and we\u2019ll show the tables we can seat.'; note.removeAttribute('data-tone'); }
      return;
    }

    var hours = hoursFor(venue, iso);
    if (!hours) {
      if (note) {
        note.textContent = 'We\u2019re closed at ' + venue.name + ' on ' + prettyDate(iso) + '. Choose another date.';
        note.setAttribute('data-tone', 'warn');
      }
      return;
    }

    var now = venueNow(tz);
    var isToday = iso === todayISO(tz);
    var earliest = isToday ? now.minutes + (venue.leadMinutes || 120) : -1;
    var last = lastSeating(venue, hours);
    var step = venue.interval || 30;

    var made = 0;
    for (var t = hours.open; t <= last; t += step) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'slot';
      btn.textContent = label(t);
      btn.setAttribute('data-time', label(t));
      btn.setAttribute('aria-pressed', 'false');
      if (t < earliest) {
        btn.disabled = true;
        btn.title = 'Too soon — we need a little notice for today';
      } else {
        made++;
      }
      wrap.appendChild(btn);
    }

    if (note) {
      if (made === 0) {
        note.textContent = isToday
          ? 'No tables left to book online today. Give us a ring on ' + venue.phone + ' and we\u2019ll see what we can do.'
          : 'No times available on this date.';
        note.setAttribute('data-tone', 'warn');
      } else {
        note.textContent = made + (made === 1 ? ' time' : ' times') + ' available. Last seating is ' + label(last) + '.';
        note.removeAttribute('data-tone');
      }
    }
  }

  /* ---------- open / closed status pill ---------- */

  function paintStatus(el, venues, tz) {
    var key = el.getAttribute('data-status-for');
    var venue = venues[key];
    /* A venue that isn't trading yet has no hours to report, so the pill is
       hidden rather than left saying "Checking hours…" forever. */
    if (!venue) { el.hidden = true; return; }
    el.hidden = false;
    var text = el.querySelector('[data-status-text]');
    var iso = todayISO(tz);
    var hours = hoursFor(venue, iso);
    var now = venueNow(tz);

    if (hours && now.minutes >= hours.open && now.minutes < hours.close) {
      el.setAttribute('data-state', 'open');
      if (text) text.textContent = 'Open now \u00b7 until ' + label(hours.close);
      return;
    }

    el.setAttribute('data-state', 'closed');
    for (var i = 0; i < 8; i++) {
      var next = addDaysISO(iso, i);
      var h = hoursFor(venue, next);
      if (!h) continue;
      if (i === 0 && now.minutes >= h.close) continue;
      if (i === 0 && now.minutes < h.open) {
        if (text) text.textContent = 'Opens ' + label(h.open) + ' today';
        return;
      }
      if (i > 0) {
        if (text) text.textContent = 'Opens ' + label(h.open) + ' ' + (i === 1 ? 'tomorrow' : DAY_NAMES[weekdayOf(next)]);
        return;
      }
    }
    if (text) text.textContent = 'Closed';
  }

  /* ---------- hours table ---------- */
  /* Written into the theme as 24-hour values because that's what the slot
     maths needs; shown to customers in the same 12-hour style as everything
     else on the page, with today's row called out. */
  function dressHours(root, tz) {
    var todayIndex = (weekdayOf(todayISO(tz)) + 6) % 7; // table starts on Monday
    root.querySelectorAll('[data-hours-table] tbody tr').forEach(function (row, i) {
      var cell = row.querySelector('td');
      if (!cell) return;
      var o = cell.getAttribute('data-open');
      var c = cell.getAttribute('data-close');
      if (o && c) {
        var om = toMinutes(o), cm = toMinutes(c);
        if (om !== null && cm !== null) cell.textContent = label(om) + ' \u2013 ' + label(cm);
      } else {
        cell.textContent = 'Closed';
      }
      row.setAttribute('data-today', i === todayIndex ? 'true' : 'false');
    });
  }

  /* ---------- validation ---------- */

  function setError(input, message) {
    var field = input.closest('.field') || input.closest('.fieldset') || input.parentNode;
    var existing = field.querySelector('.field-err');
    if (existing) existing.remove();
    if (message) {
      input.setAttribute('aria-invalid', 'true');
      var span = document.createElement('span');
      span.className = 'field-err';
      span.textContent = message;
      field.appendChild(span);
    } else {
      input.removeAttribute('aria-invalid');
    }
  }

  function validate(form, venue) {
    var problems = [];
    var checks = [
      ['first', 'Tell us your first name'],
      ['last', 'Tell us your last name'],
      ['email', 'We need an email to confirm the booking'],
      ['phone', 'A phone number lets us reach you on the day'],
      ['party', 'How many people are coming?'],
      ['date', 'Pick a date'],
      ['time', 'Choose a time from the list']
    ];

    checks.forEach(function (c) {
      var input = form.querySelector('[data-check="' + c[0] + '"]');
      if (!input) return;
      var value = (input.value || '').trim();
      if (!value) { setError(input, c[1]); problems.push(c[1]); return; }
      if (c[0] === 'email' && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
        setError(input, 'Check the email address — it looks incomplete');
        problems.push('Check the email address');
        return;
      }
      if (c[0] === 'phone' && value.replace(/[^0-9]/g, '').length < 8) {
        setError(input, 'Enter a contactable phone number');
        problems.push('Enter a contactable phone number');
        return;
      }
      setError(input, '');
    });

    var timeField = form.querySelector('[data-time-value]');
    if (timeField && !timeField.value) {
      var slotsNote = form.querySelector('[data-slots-note]');
      if (slotsNote) { slotsNote.textContent = 'Choose a time to finish your booking.'; slotsNote.setAttribute('data-tone', 'warn'); }
    }

    return problems;
  }

  /* ---------- wire up ---------- */

  function init(root) {
    var config;
    try { config = JSON.parse(root.getAttribute('data-booking-config')); } catch (e) { return; }
    if (!config || !config.locations) return;

    var tz = config.tz || 'Australia/Melbourne';
    var venues = config.locations;
    var form = root.querySelector('form');
    var dateInput = root.querySelector('[data-check="date"]');
    var timeValue = root.querySelector('[data-time-value]');
    var partyInput = root.querySelector('[data-check="party"]');
    var partyNote = root.querySelector('[data-party-note]');
    var typeField = root.querySelector('[data-enquiry-type]');
    var current = root.querySelector('input[name="contact[Location]"]:checked');
    var key = current ? current.value.toLowerCase() : Object.keys(venues)[0];

    function venue() { return venues[key] || venues[Object.keys(venues)[0]]; }

    /* Date bounds — never in the past, never absurdly far out. */
    if (dateInput) {
      dateInput.min = todayISO(tz);
      dateInput.max = addDaysISO(todayISO(tz), config.advanceDays || 90);
      /* Some layouts want today filled in already so times are visible
         without the customer touching anything. */
      if (dateInput.hasAttribute('data-default-today') && !dateInput.value) {
        dateInput.value = todayISO(tz);
      }
    }

    function refresh() {
      var iso = dateInput ? dateInput.value : '';
      drawRibbon(root, venue(), iso || todayISO(tz));
      buildSlots(root, venue(), iso, tz);
      root.querySelectorAll('[data-hours-table]').forEach(function (table) {
        table.hidden = table.getAttribute('data-hours-table') !== key;
      });
      root.querySelectorAll('[data-venue-phone]').forEach(function (el) {
        el.textContent = venue().phone;
        if (el.tagName === 'A') el.href = 'tel:' + venue().phone.replace(/[^0-9+]/g, '');
      });
    }

    root.querySelectorAll('input[name="contact[Location]"]').forEach(function (radio) {
      radio.addEventListener('change', function () {
        key = radio.value.toLowerCase();
        refresh();
      });
    });

    if (dateInput) dateInput.addEventListener('change', function () { setError(dateInput, ''); refresh(); });

    /* Slot picking — one selected at a time, mirrored into the hidden field
       so the value lands in the email Shopify sends the venue. */
    root.addEventListener('click', function (e) {
      var slot = e.target.closest('.slot');
      if (!slot || slot.disabled) return;
      root.querySelectorAll('.slot').forEach(function (s) { s.setAttribute('aria-pressed', 'false'); });
      slot.setAttribute('aria-pressed', 'true');
      if (timeValue) { timeValue.value = slot.getAttribute('data-time'); setError(timeValue, ''); }
      var note = root.querySelector('[data-slots-note]');
      if (note) { note.textContent = 'Holding ' + slot.getAttribute('data-time') + ' — send the request to confirm.'; note.removeAttribute('data-tone'); }
    });

    /* Large groups need a human, so we relabel the request rather than block it. */
    if (partyInput) {
      partyInput.addEventListener('input', function () {
        var n = parseInt(partyInput.value, 10);
        var max = venue().maxPartySize || 12;
        var big = n > max;
        if (partyNote) {
          partyNote.hidden = !big;
          partyNote.textContent = big
            ? 'Groups over ' + max + ' are arranged by phone — send this through and we\u2019ll call you, or ring ' + venue().phone + '.'
            : '';
        }
        if (typeField) typeField.value = big ? 'Large group request' : 'Table booking';
      });
    }

    if (form) {
      form.addEventListener('submit', function (e) {
        var problems = validate(form, venue());
        if (problems.length) {
          e.preventDefault();
          var first = form.querySelector('[aria-invalid="true"]');
          if (first) first.focus();
          var summary = root.querySelector('[data-form-summary]');
          if (summary) {
            summary.hidden = false;
            summary.innerHTML = '<strong>We need a couple more details</strong><ul>' +
              problems.map(function (p) { return '<li>' + p + '</li>'; }).join('') + '</ul>';
            summary.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        }
      });
    }

    dressHours(root, tz);
    refresh();

    /* Status pills elsewhere on the page share this config. */
    document.querySelectorAll('[data-status-for]').forEach(function (el) { paintStatus(el, venues, tz); });
    setInterval(function () {
      document.querySelectorAll('[data-status-for]').forEach(function (el) { paintStatus(el, venues, tz); });
    }, 60000);
  }

  function boot() {
    document.querySelectorAll('[data-booking-config]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
